<p-sidebar
  [(visible)]="visible"
  (onHide)="hide()"
  position="right"
  [modal]="true"
  [dismissible]="true"
  [showCloseIcon]="true"
  [blockScroll]="true"
  ariaCloseLabel="Cerrar"
  appendTo="body"
  styleClass="payment-drawer"
>
  <ng-template pTemplate="header">
    <div class="pdw-head" *ngIf="payment">
      <span class="pdw-eyebrow">Pago</span>
      <h3 class="pd-mono">{{ payment.trackingKey }}</h3>
      <span class="pdw-meta">{{ payment.amount | currency }} · {{ payment.paymentMethod }} · {{ payment.account || 'sin cuenta' }}</span>
    </div>
  </ng-template>

  <ng-container *ngIf="payment">
    <!-- Los datos van sueltos, no en una tarjeta cada uno: seis cajas para seis valores cortos gastaban
         el ancho del cajón y aplanaban la jerarquía. Lo que agrupa son las líneas de sección. -->
    <section class="pdw-section">
      <h4>Las dos fechas</h4>
      <div class="pdw-facts">
        <div class="pdw-fact">
          <span class="pdw-fact-l">Pagó el cliente</span>
          <span class="pdw-fact-v">{{ payment.paidOn | date: 'dd/MM/yyyy' : 'UTC' }}</span>
        </div>
        <div class="pdw-fact" [class.is-late]="payment.registrationLagDays > 0">
          <span class="pdw-fact-l">Lo registramos</span>
          <span class="pdw-fact-v">{{ payment.registeredOn | date: 'dd/MM/yyyy HH:mm' }}</span>
          <span
            *ngIf="payment.registrationLagDays > 0"
            class="enf-chip pdw-fact-tag"
            tabindex="0"
            [attr.aria-label]="
              'Registrado ' +
              payment.registrationLagDays +
              (payment.registrationLagDays === 1 ? ' día' : ' días') +
              ' después de que el cliente pagó'
            "
            >{{ payment.registrationLagDays }} d tarde</span
          >
        </div>
        <div class="pdw-fact">
          <span class="pdw-fact-l">Registró</span>
          <span class="pdw-fact-v">{{ payment.registeredBy || '—' }}</span>
        </div>
        <div class="pdw-fact">
          <span class="pdw-fact-l">Estado</span>
          <span class="pdw-fact-v">{{ statusLabel(payment.status) }}</span>
        </div>
      </div>
    </section>

    <!-- El dinero pesa más que las fechas: mismo patrón, valor más grande (derivado del libro, no de
         la copia del pago). -->
    <section class="pdw-section">
      <div class="pdw-facts is-money">
        <div class="pdw-fact">
          <span class="pdw-fact-l">Aplicado</span>
          <span class="pdw-fact-v">{{ payment.applied | currency }}</span>
        </div>
        <div class="pdw-fact">
          <span class="pdw-fact-l">Disponible</span>
          <span class="pdw-fact-v" [class.is-good]="payment.pendingToApply > 0">{{ payment.pendingToApply | currency }}</span>
        </div>
      </div>
    </section>

    <!-- A dónde fue el dinero -->
    <section class="pdw-section">
      <h4>
        A dónde fue el dinero <small *ngIf="otherLeases">· {{ otherLeases }} arrendamiento(s) más</small>
      </h4>
      <ul class="pdw-dest" *ngIf="payment.destinations.length; else sinDestino">
        <li *ngFor="let a of payment.destinations" [class.is-cancelled]="a.status === 'CANCELLED'">
          <div class="pdw-dest-h">
            <span class="pdw-dest-k">{{ destinationLabel(a) }}</span>
            <span class="pdw-dest-amt">{{ a.amount | currency }}</span>
          </div>
          <div class="pdw-dest-meta">
            <span>aplicó {{ a.appliedBy || '—' }} el {{ a.appliedOn | date: 'dd/MM/yyyy' }}</span>
            <!-- La acción vive en la línea de su aplicación, no flotando sola al final del bloque. -->
            <button
              *appIfHasPermission="['applications', 'delete']"
              pButton
              type="button"
              label="Cancelar aplicación"
              icon="pi pi-ban"
              class="p-button-text p-button-sm p-button-danger pdw-dest-cancel"
              [hidden]="a.status === 'CANCELLED'"
              (click)="
                cancelApplication.emit({
                  applicationId: a.applicationId,
                  paymentId: payment!._id,
                  n: a.n,
                  letters: letterNumbers(a),
                  amount: a.amount,
                })
              "
            ></button>
          </div>
          <!-- B-039 · Reparto por letra cuando el pago tocó varias: cuánto le tocó a cada una, saldada o no -->
          <ul class="pdw-letters" *ngIf="letterBreakdown(a).length">
            <li *ngFor="let l of letterBreakdown(a)">
              <span>{{ l.n !== null ? 'Letra ' + l.n : 'Letra sin número' }}</span>
              <span class="pdw-letters-amt">{{ l.amount | currency }}</span>
            </li>
          </ul>
          <!-- Reparto por concepto: Interés · Mora · Accesorios · Capital · IVA -->
          <div class="pdw-concepts">
            <span *ngFor="let k of conceptKeys(a.byConcept)">{{ conceptLabels[k] || k }} {{ a.byConcept[k] | currency }}</span>
          </div>
        </li>
      </ul>
      <ng-template #sinDestino><p class="pd-empty">Todavía no se ha aplicado a ninguna letra.</p></ng-template>
    </section>

    <!-- Evidencia de posible duplicado (U4: evidencia, no veredicto) -->
    <section class="pdw-section pdw-twins" *ngIf="payment.twins?.length">
      <h4><i class="pi pi-clone"></i> Posible duplicado</h4>
      <p class="pd-empty">
        Hay {{ payment.twins?.length }} pago(s) del mismo cliente con el mismo monto y el mismo día o la misma clave. Es evidencia, no
        veredicto: revisa antes de anular.
      </p>
      <p class="pd-empty" *ngIf="activeLeases > 0">
        El cliente tiene <strong>{{ activeLeases }}</strong> arrendamiento(s) activo(s): la misma renta en cada uno es un caso normal.
      </p>
      <ul class="pdw-list">
        <li *ngFor="let t of payment.twins || []">
          <span class="pd-mono">{{ t.trackingKey }}</span>
          <span class="pdw-amount">{{ t.amount | currency }}</span>
          <span>{{ t.date | date: 'dd/MM/yyyy' : 'UTC' }} · {{ statusLabel(t.status) }}</span>
          <span class="text-color-secondary" *ngIf="t.leases?.length"
            >{{ t.leases[0].folio }} · letras {{ t.leases[0].letters.join(', ') }}</span
          >
        </li>
      </ul>
    </section>

    <!-- Bitácora del pago: registro · cada aplicación · ajustes -->
    <section class="pdw-section">
      <h4>Bitácora del pago</h4>
      <!-- Misma rejilla que «Movimientos de la letra» y que el historial de Cartera. Antes era un
           `p-timeline`, cuya columna opuesta vacía se llevaba media anchura del cajón. -->
      <div class="pdw-events" *ngIf="bitacora.length; else sinBitacora">
        <div class="pdw-ev" *ngFor="let e of bitacora">
          <span class="pdw-ev-dt">{{ e.date | date: 'dd/MM/yyyy HH:mm' }}</span>
          <span class="pdw-ev-dot" aria-hidden="true"></span>
          <span class="pdw-ev-body">
            <span
              ><b>{{ e.label }}</b
              ><span *ngIf="e.amount != null" class="pdw-ev-amt"> · {{ e.amount | currency }}</span></span
            >
            <span class="pdw-ev-who" *ngIf="e.user">{{ e.user }}</span>
            <span class="pdw-ev-who" *ngIf="e.reason">{{ e.reason }}</span>
          </span>
        </div>
      </div>
      <ng-template #sinBitacora><p class="pd-empty">Sin movimientos registrados.</p></ng-template>
    </section>
  </ng-container>

  <ng-template pTemplate="footer">
    <div class="pdw-actions" *ngIf="payment">
      <ng-container *appIfHasPermission="['applications', 'create']">
        <button
          *ngIf="payment.pendingToApply > 0"
          pButton
          type="button"
          label="Aplicar"
          icon="pi pi-check"
          class="p-button-sm p-button-success"
          (click)="apply.emit(payment)"
        ></button>
      </ng-container>
      <ng-container *appIfHasPermission="['payments', 'adjust']">
        <button
          *ngIf="!payment.deletedAt"
          pButton
          type="button"
          label="Corregir fecha"
          icon="fa-solid fa-calendar-day"
          class="p-button-sm p-button-text"
          (click)="changeDate.emit(payment)"
        ></button>
        <button
          pButton
          type="button"
          label="Ajustar"
          icon="fa-solid fa-wrench"
          class="p-button-sm p-button-outlined"
          (click)="adjust.emit(payment)"
        ></button>
      </ng-container>
    </div>
  </ng-template>
</p-sidebar>
